/**
 * ConversationHistory Component
 *
 * Sidebar showing past AI conversations that can be restored.
 */
import React, { useCallback } from 'react';
import { List, Typography, Button, Empty, Spin, Tooltip, Popconfirm } from 'antd';
import {
  HistoryOutlined,
  PlusOutlined,
  DeleteOutlined,
  MessageOutlined,
  UserOutlined,
  RobotOutlined
} from '@ant-design/icons';
import type { Conversation } from '@/hooks/ai-colleagues/useConversationHistory';
import styles from './ConversationHistory.module.less';

const { Text } = Typography;

// Colleague icons mapping
const COLLEAGUE_ICONS: Record<string, string> = {
  alex: '🛡️',
  morgan: '🔗',
  jordan: '💳',
  sam: '💬',
  riley: '⚙️',
  taylor: '🏛️',
  casey: '💳',
  general: '🤖'
};

const COLLEAGUE_NAMES: Record<string, string> = {
  alex: 'Ivan',
  morgan: 'Simpson',
  jordan: 'Jordan',
  sam: 'Carla',
  riley: 'Riley',
  taylor: 'Karl',
  casey: 'Leon',
  general: 'Assistant'
};

type ConversationHistoryProps = {
  conversations: Conversation[];
  isLoading: boolean;
  activeId: string | null;
  onSelect: (conversation: Conversation) => void;
  onNewConversation: () => void;
  onArchive: (id: string) => void;
};

/**
 * Format relative time
 */
function formatRelativeTime(date: Date): string {
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / (1000 * 60));
  const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;
  return date.toLocaleDateString();
}

export const ConversationHistory: React.FC<ConversationHistoryProps> = ({
  conversations,
  isLoading,
  activeId,
  onSelect,
  onNewConversation,
  onArchive
}) => {
  const handleArchive = useCallback((e: React.MouseEvent, id: string) => {
    e.stopPropagation();
    onArchive(id);
  }, [onArchive]);

  return (
    <div className={styles.container}>
      {/* Header */}
      <div className={styles.header}>
        <div className={styles.headerTitle}>
          <HistoryOutlined />
          <Text strong>History</Text>
        </div>
        <Tooltip title="New conversation">
          <Button
            type="text"
            size="small"
            icon={<PlusOutlined />}
            onClick={onNewConversation}
            className={styles.newButton}
          />
        </Tooltip>
      </div>

      {/* Content */}
      <div className={styles.content}>
        {isLoading ? (
          <div className={styles.loading}>
            <Spin size="small" />
          </div>
        ) : conversations.length === 0 ? (
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description={
              <Text type="secondary" className={styles.emptyText}>
                No conversations yet
              </Text>
            }
            className={styles.empty}
          />
        ) : (
          <List
            dataSource={conversations}
            renderItem={(conversation) => (
              <div
                key={conversation.id}
                className={`${styles.item} ${activeId === conversation.id ? styles.active : ''}`}
                onClick={() => onSelect(conversation)}
              >
                <div className={styles.itemIcon}>
                  {COLLEAGUE_ICONS[conversation.colleagueId] || '🤖'}
                </div>
                <div className={styles.itemContent}>
                  <div className={styles.itemTitle}>
                    {conversation.title || 'New conversation'}
                  </div>
                  <div className={styles.itemMeta}>
                    <span className={styles.colleagueName}>
                      {COLLEAGUE_NAMES[conversation.colleagueId] || 'Assistant'}
                    </span>
                    <span className={styles.dot}>·</span>
                    <span className={styles.time}>
                      {formatRelativeTime(conversation.updatedAt)}
                    </span>
                  </div>
                </div>
                <Popconfirm
                  title="Archive this conversation?"
                  description="You can view archived conversations later."
                  onConfirm={(e) => handleArchive(e as unknown as React.MouseEvent, conversation.id)}
                  okText="Archive"
                  cancelText="Cancel"
                  placement="left"
                >
                  <Button
                    type="text"
                    size="small"
                    icon={<DeleteOutlined />}
                    className={styles.deleteButton}
                    onClick={(e) => e.stopPropagation()}
                  />
                </Popconfirm>
              </div>
            )}
          />
        )}
      </div>
    </div>
  );
};

export default ConversationHistory;
